import React, { useState } from 'react';
import { useCCContext, Basket } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import styles from './BasketManager.module.css';

interface BasketManagerProps {
  objectId: string;
}

interface MessageState {
  type: 'error' | 'success' | '';
  text: string;
}

interface CreateBasketResponse {
  basketId?: string;
  error?: string;
}

const BasketManager: React.FC<BasketManagerProps> = ({ objectId }) => {
  const { user, setUser } = useCCContext();
  const [selectedBasket, setSelectedBasket] = useState<string>('');
  const [newBasketName, setNewBasketName] = useState<string>('');
  const [isCreatingBasket, setIsCreatingBasket] = useState<boolean>(false);
  const [message, setMessage] = useState<MessageState>({ type: '', text: '' });

  // Get baskets that contain this object
  const basketsWithObject = user?.baskets.filter(basket => 
    basket.items.includes(objectId)
  ) || [];

  const handleRemoveFromBasket = async (basketName: string): Promise<void> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=removeFromBasket&basketName=${encodeURIComponent(basketName)}&objectId=${encodeURIComponent(objectId)}&token=${user?.token}`
      );
      const text = await response.text();
      
      if (text.includes('successfully') && user) {
        setUser({
          ...user,
          baskets: user.baskets.map(basket => 
            basket.basketName === basketName
              ? { ...basket, items: basket.items.filter(item => item !== objectId) }
              : basket
          )
        });
        setMessage({ type: 'success', text: 'Removed from basket successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to remove from basket' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error removing from basket' });
    }
  };

  const handleCreateBasket = async (): Promise<void> => {
    if (!newBasketName.trim()) {
      setMessage({ type: 'error', text: 'Please enter a basket name' });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=createBasket&basketName=${encodeURIComponent(newBasketName)}&token=${user?.token}`
      );
      const data: CreateBasketResponse = await response.json();
      
      if (data.basketId && user) {
        const newBasket: Basket = {
          basketId: data.basketId,
          basketName: newBasketName,
          created: new Date().toISOString(),
          altered: new Date().toISOString(),
          items: []
        };

        setMessage({ type: 'success', text: 'Basket created successfully' });
        setNewBasketName('');
        setIsCreatingBasket(false);
        
        // Update the user context with the new basket
        setUser({
          ...user,
          baskets: [...user.baskets, newBasket]
        });
        
        // Select the newly created basket
        setSelectedBasket(newBasket.basketName);
      } else {
        setMessage({ type: 'error', text: 'Failed to create basket' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error creating basket' });
    }
  };

  const handleAddToBasket = async (): Promise<void> => {
    if (!selectedBasket) {
      setMessage({ type: 'error', text: 'Please select a basket' });
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_USERMANAGEMENT_URL}?action=addToBasket&basketName=${encodeURIComponent(selectedBasket)}&objectId=${encodeURIComponent(objectId)}&token=${user?.token}`
      );
      const text = await response.text();
      
      if (text.includes('successfully') && user) {
        setUser({
          ...user,
          baskets: user.baskets.map(basket => 
            basket.basketName === selectedBasket
              ? { ...basket, items: [...basket.items, objectId] }
              : basket
          )
        });
        setMessage({ type: 'success', text: 'Added to basket successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to add to basket' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error adding to basket' });
    }
  };

  const handleComboboxChange = (value: string) => {
    if (value === 'create-new') {
      setIsCreatingBasket(true);
      setSelectedBasket('');
    } else {
      setSelectedBasket(value);
      setIsCreatingBasket(false);
    }
  };

  const containerStyles: React.CSSProperties = {
    padding: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px'
  };

  const flexContainerStyles: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '8px'
  };

  const selectStyles: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    minWidth: '200px'
  };

  const inputStyles: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    minWidth: '200px'
  };

  const buttonStyles: React.CSSProperties = {
    padding: '6px 12px',
    borderRadius: '4px',
    backgroundColor: '#4b5563',
    color: 'white',
    border: 'none',
    cursor: 'pointer'
  };

  const messageStyles: React.CSSProperties = {
    padding: '8px',
    marginBottom: '8px',
    borderRadius: '4px',
    backgroundColor: message.type === 'error' ? '#fee2e2' : '#dcfce7',
    color: message.type === 'error' ? '#991b1b' : '#166534'
  };

  return (
    <div style={containerStyles}>
      {message.text && (
        <div style={messageStyles}>
          {message.text}
        </div>
      )}

      {/* Display existing baskets containing the object */}
      {basketsWithObject.length > 0 && (
        <div className={styles.existingBaskets}>
          {basketsWithObject.map((basket) => (
            <div key={basket.basketId} className={styles.basketWithObject}>
              {basket.basketName}
              <button
                className={styles.removeButton}
                onClick={() => handleRemoveFromBasket(basket.basketName)}
                aria-label={`Remove from ${basket.basketName}`}
              >
                X
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Hide Add to basket
      <div style={flexContainerStyles}>
        <select 
          value={selectedBasket}
          onChange={(e) => handleComboboxChange(e.target.value)}
          style={selectStyles}
        >
          <option value="">Select a basket</option>
          <option value="create-new">Create new basket</option>
          {user?.baskets.map((basket) => (
            <option key={basket.basketId} value={basket.basketName}>
              {basket.basketName}
            </option>
          ))}
        </select>

        {isCreatingBasket ? (
          <>
            <input
              type="text"
              value={newBasketName}
              onChange={(e) => setNewBasketName(e.target.value)}
              placeholder="Enter new basket name"
              style={inputStyles}
            />
            <GenericIconButton 
              icon="regular/check.svg"
              onClick={handleCreateBasket}
              ariaLabel="Confirm create basket"
            />
            <GenericIconButton 
              icon="light/times.svg"
              onClick={() => {
                setIsCreatingBasket(false);
                setNewBasketName('');
              }}
              ariaLabel="Cancel create basket"
            />
          </>
        ) : (
          <button 
            onClick={handleAddToBasket}
            style={buttonStyles}
            disabled={!selectedBasket}
          >
            Add to Basket
          </button>
        )}
      </div>
      */}
    </div>
  );
};

export default BasketManager;
